import React from "react";
import { Button } from "@instructure/ui-buttons";
import { Spinner } from "@instructure/ui-spinner";
import { IconUploadLine } from "@instructure/ui-icons";
import { submitDtiMetric, getDtiContext } from "./api/api";
import { getSubmitFeedback } from "./api/submit-utils";
import LMSInfo from "./components/LMSInfo";
import LCMSInfo from "./components/LCMSInfo";
import DigitalLearning from "./components/DigitalLearning";
import TeacherLessionRate from "./components/TeacherLessionRate";
import StudentOnlineRate from "./components/StudentOnlineRate";
import AssessmentTracking from "./components/AssessmentTracking";
import DigitalLession from "./components/DigitalLession";
import "./common/dti.css";
import Interactive from "./components/Interactive";

const DTI_SECTIONS = [
  {
    id: "section-3-3",
    code: "3.3",
    title: "Thống kê dữ liệu hành vi người dùng",
    hint: "Thống kê dữ liệu hành vi người dùng",
    Component: Interactive,
    props: {},
  },
  {
    id: "section-4-4",
    code: "4.4",
    title: "Tỉ lệ giáo viên tổ chức dạy học trực tuyến / trực tiếp",
    hint: "Số giáo viên đã tạo bài giảng và tổ chức hoạt động dạy học trên LMS",
    Component: DigitalLession,
    props: {},
  },
  {
    id: "section-6-1",
    code: "6.1",
    title: "Hệ thống quản lý học tập trực tuyến (LMS)",
    hint: "Thống kê bài giảng, bài kiểm tra, lớp học trực tuyến và học sinh hoạt động trên LMS",
    Component: LMSInfo,
    props: {},
    submitMetricId: "lms",
  },
  {
    id: "section-6-2",
    code: "6.2",
    title: "Hệ thống quản lý nội dung học tập trực tuyến (LCMS)",
    hint: "Số lượng học liệu, bài giảng số và nội dung được quản lý trên LCMS",
    Component: LCMSInfo,
    props: {},
  },
  {
    id: "section-6-3",
    code: "6.3",
    title: "Số lượng học liệu số",
    hint: "Tổng số lớp có học liệu, tỉ lệ lớp có học liệu và triển khai CTGDPT trực tuyến",
    Component: DigitalLearning,
    props: {},
  },
  {
    id: "section-6-4",
    code: "6.4",
    title: "Học sinh tham gia hoạt động trực tuyến",
    hint: "Số học sinh có phát sinh hoạt động học tập trên hệ thống",
    Component: StudentOnlineRate,
    props: {},
  },
  {
    id: "section-6-5",
    code: "6.5",
    title: "Giáo viên xây dựng bài giảng tương tác",
    hint: "Số giáo viên, bài giảng tương tác và mức độ học sinh tham gia",
    Component: TeacherLessionRate,
    props: {},
    submitMetricId: "teacher-interactive",
  },
  {
    id: "section-6-6",
    code: "6.6",
    title: "Kiểm tra đánh giá thường xuyên trực tuyến (ĐGTX)",
    hint: "Bài kiểm tra trực tuyến, học sinh làm bài và thống kê theo môn / khối",
    Component: AssessmentTracking,
    props: {},
  },
];

const DtiSectionBlock = React.memo(function DtiSectionBlock({
  id,
  code,
  title,
  hint,
  Component,
  componentProps,
  submitMetricId,
  schoolLabel,
  yearLabel,
}) {
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitSuccess, setSubmitSuccess] = React.useState(null);
  const [submitError, setSubmitError] = React.useState(null);

  const handleSubmit = async () => {
    if (!submitMetricId || isSubmitting) return;
    setIsSubmitting(true);
    setSubmitSuccess(null);
    setSubmitError(null);
    try {
      const result = await submitDtiMetric(submitMetricId);
      const { ok, message } = getSubmitFeedback(result);
      if (ok) setSubmitSuccess(message);
      else setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="dti-section-card" id={id}>
      <div className="dti-section-card__header">
        {(schoolLabel || yearLabel) && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}>
            {schoolLabel && <span className="dti-section-card__context-item">{schoolLabel}</span>}
            <span className="dti-section-card__context-item" style={{ padding: '0px 5px' }}> • </span>
            {yearLabel && <span className="dti-section-card__context-item">{yearLabel}</span>}
          </div>
        )}
        <div className="dti-section-card__header-top">
          <span className="dti-section-card__code">Tiêu chí {code}</span>
          {submitMetricId && (
            <Button
              onClick={handleSubmit}
              color="primary"
              size="small"
              disabled={isSubmitting}
              renderIcon={isSubmitting ? undefined : IconUploadLine}
            >
              {isSubmitting ? (
                <Spinner renderTitle="Đang nộp" size="x-small" />
              ) : (
                "Nộp điểm DTI"
              )}
            </Button>
          )}
        </div>
        {(submitSuccess || submitError) && (
          <p
            className={
              submitError
                ? "dti-section-card__submit-status dti-section-card__submit-status--error"
                : "dti-section-card__submit-status"
            }
          >
            {submitSuccess || submitError}
          </p>
        )}
        <h2 className="dti-section-card__title">{title}</h2>
        <p className="dti-section-card__hint">{hint}</p>
      </div>
      <div className="dti-section-card__body">
        <Component embedded {...componentProps} />
      </div>
    </section>
  );
});

export default function ShowDTI() {
  const [context, setContext] = React.useState(null);

  React.useEffect(() => {
    getDtiContext()
      .then((data) => {
        if (data && !data.status) setContext(data);
      })
      .catch(() => {});
  }, []);

  const schoolLabel =
    context?.schoolName ||
    (context?.schemaName ? context.schemaName : null);
  const yearLabel = context?.academicYear;

  return (
    <div className="dti-page">
      <header className="dti-hero">
        <span className="dti-hero__badge">Báo cáo DTI · Chuyển đổi số giáo dục</span>
        <h1 className="dti-hero__title">Tổng hợp chỉ số DTI của đơn vị</h1>
        {(schoolLabel || yearLabel) && (
          <div className="dti-hero__meta">
            {schoolLabel && (
              <span className="dti-hero__meta-item">
                {schoolLabel}
              </span>
            )}
              <span className="dti-hero__meta-item" style={{ padding: '0px 5px' }}>
                •
              </span>
            {yearLabel && (
              <span className="dti-hero__meta-item">
                {yearLabel}
              </span>
            )}
          </div>
        )}
      </header>

      <main className="dti-main">
        {DTI_SECTIONS.map((section) => (
          <DtiSectionBlock
            key={section.id}
            id={section.id}
            code={section.code}
            title={section.title}
            hint={section.hint}
            Component={section.Component}
            componentProps={section.props}
            submitMetricId={section.submitMetricId}
            schoolLabel={schoolLabel}
            yearLabel={yearLabel}
          />
        ))}
      </main>
    </div>
  );
}
